import { PauseIcon, PlayIconFilledIcon } from 'assets/icons';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { appActions, AppContext } from 'context';
import { NotificationObject } from 'models/notificationObject';
import { NotificationType } from 'common/enums';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import audioLottie from 'assets/lottie/audio.json';
import styles from './audio-player.module.scss';

interface AudioPlayerProps {
  // Play/pause control shown over the waveform; the viewer's bottom bar no
  // longer has one.
  onTogglePlay?: () => void;
  audioUrl: string;
  isPlaying: boolean;
  onFinish?: () => void;
  // Played to the end with nowhere to advance to: the parent should show it
  // stopped (play button back, animation off).
  onEnded?: () => void;
  isFullScreen: boolean;
  showControls?: boolean;
}

const formatTime = (timeInSeconds: number): string => {
  if (Number.isNaN(timeInSeconds) || timeInSeconds < 0) return '00:00';
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  onTogglePlay,
  audioUrl,
  isPlaying,
  onFinish,
  onEnded,
  isFullScreen,
  showControls = false,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const lottieRef = useRef<LottieRefCurrentProps | null>(null);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const { dispatch } = useContext(AppContext);
  // Read at the end of playback, so the listener below needn't be re-bound
  // every time the parent passes a new function.
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      lottieRef.current?.play();
      audio.play().catch(() =>
        dispatch({
          type: appActions.NOTIFICATION,
          notification: new NotificationObject({
            show: true,
            type: NotificationType.Error,
            title: 'Error playing audio',
          }),
        }),
      );
    } else {
      lottieRef.current?.pause();
      audio.pause();
    }
  }, [isPlaying, audioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);

    // Auto-advance moves on to the next memory; otherwise the clip stops and
    // rewinds, ready to play again.
    const handleEnd = () => {
      if (onFinish) {
        onFinish();
        return;
      }
      audio.currentTime = 0;
      setCurrentTime(0);
      lottieRef.current?.stop();
      onEndedRef.current?.();
    };
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnd);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnd);
    };
  }, [audioUrl, onFinish]);

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className={styles.container}>
      <div className={styles.recordingIcon}>
        <Lottie lottieRef={lottieRef} animationData={audioLottie} loop />
        {onTogglePlay && (
          <button
            type="button"
            className={styles.playToggle}
            onClick={onTogglePlay}
            aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
          >
            {isPlaying ? <PauseIcon /> : <PlayIconFilledIcon />}
          </button>
        )}
      </div>
      {!isFullScreen && !showControls && (
        <div className={styles.audioPlayerContainer}>
          <span className={styles.currentTimeDisplay}>
            {formatTime(currentTime)}
          </span>
          <div className={styles.progressBarWrapper}>
            <div
              className={styles.progressBarFill}
              style={{ width: `${progressPercentage}%` }}
            />
            <input
              type="range"
              min={0}
              max={duration}
              step={0.01}
              value={currentTime}
              onChange={(e) => {
                if (audioRef.current) {
                  audioRef.current.currentTime = parseFloat(e.target.value);
                  setCurrentTime(parseFloat(e.target.value));
                }
              }}
              className={styles.seekBar}
            />
          </div>

          <span className={styles.totalTimeDisplay}>
            {formatTime(duration)}
          </span>
        </div>
      )}
      <audio
        controls={showControls}
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
      >
        <track kind="captions" />
      </audio>
    </div>
  );
};
